import { DEFAULT_PAGE_SIZE, MAX_PAGE, PAGE_SIZE_OPTIONS } from '@/definitions/pagination'

export interface DirectoryParams {
  /** 0-indexed, matching both the backend envelope and MUI's TablePagination. */
  page: number
  limit: number
  /** Blank when no search is active. */
  search: string
}

const DEFAULT_PARAMS: DirectoryParams = {
  page: 0,
  limit: DEFAULT_PAGE_SIZE,
  search: '',
}

/**
 * Read directory state out of the URL, falling back to defaults for anything
 * missing or malformed.
 *
 * Values come from the address bar, so they are hostile input: a hand-edited
 * `limit` outside the offered sizes, a negative, fractional, or out-of-bounds
 * page, or plain junk all resolve to the default rather than reaching the API,
 * which would reject them with a validation error.
 */
export const parseDirectoryParams = (params: URLSearchParams): DirectoryParams => {
  const page = Number(params.get('page'))
  const limit = Number(params.get('limit'))

  return {
    page:
      Number.isInteger(page) && page >= 0 && page <= MAX_PAGE
        ? page
        : DEFAULT_PARAMS.page,
    limit: PAGE_SIZE_OPTIONS.includes(limit) ? limit : DEFAULT_PARAMS.limit,
    search: (params.get('search') ?? '').trim(),
  }
}

/**
 * Apply a change to the current directory state.
 *
 * Changing the search term or the page size returns the user to the first page —
 * staying on page 5 of a result set that just shrank would show an empty list.
 */
export const nextDirectoryParams = (
  current: DirectoryParams,
  changes: Partial<DirectoryParams>
): DirectoryParams => {
  const merged = { ...current, ...changes }
  const searchChanged = changes.search !== undefined && changes.search !== current.search
  const limitChanged = changes.limit !== undefined && changes.limit !== current.limit

  return searchChanged || limitChanged ? { ...merged, page: 0 } : merged
}

/**
 * Serialise directory state for the address bar, omitting anything at its
 * default so a pristine view has a clean URL.
 */
export const toSearchParams = (params: DirectoryParams): URLSearchParams => {
  const search = new URLSearchParams()

  if (params.page !== DEFAULT_PARAMS.page) {
    search.set('page', String(params.page))
  }
  if (params.limit !== DEFAULT_PARAMS.limit) {
    search.set('limit', String(params.limit))
  }
  if (params.search) {
    search.set('search', params.search)
  }

  return search
}
